import { html, isServer, unsafeCSS } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import EkoElement from "@/utilities/element";
import { createSlotChangeHandler } from "@/utilities/slot";
import style from "./shortcut.css?inline";

// Determine whether OS is Mac (must only be called on the client)
function isMac(): boolean {
  if ("userAgentData" in navigator) {
    const platform = (navigator as any).userAgentData.platform || "";
    return platform.toLowerCase().includes("mac");
  }

  // Fallback to navigator.platform (deprecated)
  return navigator.platform.toLowerCase().includes("mac");
}

const KEY_ALT = "⎇";
const KEY_SHIFT = "⇧";

@customElement("eko-shortcut")
export class Shortcut extends EkoElement {
  static styles = [...EkoElement.styles, unsafeCSS(style)];

  private handleSlotChange = createSlotChangeHandler(this, {});

  @state()
  private hasSlotContent = false;

  @state()
  private mac = false;

  @property({ reflect: true, type: Boolean })
  alt?: boolean;

  @property({ reflect: true, type: Boolean })
  ctrl?: boolean;

  @property({ reflect: true, type: Boolean })
  shift?: boolean;

  private getAltKey(): string {
    return this.mac ? "⌥" : KEY_ALT;
  }

  private getCtrlKey(): string {
    return this.mac ? "⌘" : "^";
  }

  private getKeyTitle(keySymbol: string, ctrlKey: string): string {
    if (keySymbol === ctrlKey) {
      return ctrlKey === "⌘" ? "Command key" : "Control key";
    }
    if (keySymbol === KEY_ALT) {
      return ctrlKey === "⌘" ? "Option key" : "Alt key";
    }
    if (keySymbol === KEY_SHIFT) return "Shift key";
    return "";
  }

  firstUpdated(): void {
    if (!isServer) {
      this.mac = isMac();
    }
  }

  render() {
    const ctrlKey = this.getCtrlKey();
    const altKey = this.getAltKey();
    const keys = [];
    if (this.ctrl) keys.push(ctrlKey);
    if (this.alt) keys.push(altKey);
    if (this.shift) keys.push(KEY_SHIFT);

    return html`
      ${keys.map(
        (key) => html`<kbd part="root" title="${this.getKeyTitle(key, ctrlKey)}">${key}</kbd>`
      )}
      ${this.hasSlotContent
        ? html`<kbd part="root"><slot @slotchange=${this.handleSlotChange}></slot></kbd>`
        : html`<slot @slotchange=${this.handleSlotChange}></slot>`}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "eko-shortcut": Shortcut;
  }
}
